/**
 * Login Form
 * Standalone login form with theme toggle
 * 
 * Related auth forms are in separate modules:
 * - forgotPassword.ts - Password recovery modal
 * - signup.ts - Account registration modal
 * - reconnect.ts - Session reconnection modal
 * - lockScreen.ts - Lock screen overlay
 */

import { themeManager } from '@/services/ThemeManager';
import { ToasterService } from '@/services/ToasterService';
import { authService } from '@/services/Authentication';
import { auth0Service } from '@/services/Auth0Service';
import { Logger } from '@/services/Logger';

// Import auth modals from focused modules
import { showForgotPasswordModal } from './forgotPassword';
import { showSignupModal } from './signup';

// Re-export for consumers that import from login.ts
export { showForgotPasswordModal } from './forgotPassword';
export { showSignupModal } from './signup';
export { showReconnectModal } from './reconnect';
export { showLockScreen, hideLockScreen } from './lockScreen';

const logger = Logger.scope('Login');

/**
 * Initialize the login form (called by FormLoader)
 */
export async function init(): Promise<void> {
  logger.debug('Login form init() called');
  
  // Ensure ToasterService is initialized (creates toast container if needed)
  ToasterService.init();
  
  setupThemeToggle();
  setupLoginSubmit();
  setupSocialLogin();
  setupForgotPasswordLink();
  setupRequestAccessLink();
}

/**
 * Setup social login button handler
 */
function setupSocialLogin(): void {
  const socialBtn = document.getElementById('login-social-btn');
  
  if (!socialBtn) {
    logger.warn('Social login button not found');
    return;
  }

  socialBtn.addEventListener('click', async () => {
    try {
      const auth0User = await auth0Service.login();
      
      // Show progress
      ToasterService.info('Authenticating with server...');
      
      // Exchange Auth0 token with TriSys Web API
      const result = await authService.loginWithAuth0(auth0User.email, auth0User);
      
      if (result.success) {
        ToasterService.dismissAll();
        ToasterService.success(`Welcome, ${result.user?.name || auth0User.email}!`);
        
        // Close the login overlay if it exists
        await new Promise(resolve => setTimeout(resolve, 300));
        const loginOverlay = document.getElementById('login-overlay');
        if (loginOverlay) {
          loginOverlay.remove();
        }
      } else {
        ToasterService.error(result.message || 'Login failed. Please try again.');
      }
      
    } catch (error) {
      // Error is already logged/toasted by Auth0Service
      logger.warn('Social login flow interrupted');
    }
  });
}

/**
 * Setup theme toggle button for login form
 */
function setupThemeToggle(): void {
  const toggleBtn = document.getElementById('theme-toggle');
  const icon = document.getElementById('theme-icon');
  
  // Find the login overlay container (created by authentication.ts)
  // or the main container div (when loaded standalone)
  const loginOverlay = document.getElementById('login-overlay');
  const loginContainer = document.querySelector('.min-h-screen[data-theme]') as HTMLElement;

  logger.debug('setupThemeToggle', { toggleBtn: !!toggleBtn, icon: !!icon, overlay: !!loginOverlay, container: !!loginContainer });

  if (!toggleBtn || !icon) {
    logger.warn('Theme toggle elements not found');
    return;
  }

  // Initialize theme manager and get current theme
  themeManager.init();
  let currentTheme = themeManager.getCurrent();

  logger.debug('Initial theme:', currentTheme);

  // Set initial icon and theme based on stored/current theme
  updateIcon(toggleBtn, icon, currentTheme);
  
  // Apply initial theme to the appropriate container
  if (loginOverlay) {
    loginOverlay.setAttribute('data-theme', currentTheme);
  }
  if (loginContainer) {
    loginContainer.setAttribute('data-theme', currentTheme);
  }

  // Toggle theme on click
  toggleBtn.addEventListener('click', () => {
    logger.debug('Theme toggle clicked, current theme:', currentTheme);
    currentTheme = currentTheme === 'dark' ? 'light' : 'dark';
    
    // Apply theme using ThemeManager (handles persistence and document.documentElement)
    themeManager.apply(currentTheme);
    
    // Apply to both overlay and container for immediate visual feedback
    if (loginOverlay) {
      loginOverlay.setAttribute('data-theme', currentTheme);
    }
    if (loginContainer) {
      loginContainer.setAttribute('data-theme', currentTheme);
    }
    
    // Update the left side overlay based on theme
    updateLeftSideOverlay(currentTheme);
    
    // Update icon and tooltip
    updateIcon(toggleBtn, icon, currentTheme);
    logger.debug('Theme changed to:', currentTheme);
  });
  
  // Apply initial left side overlay
  updateLeftSideOverlay(currentTheme);
}

/**
 * Update the left side overlay based on theme
 */
function updateLeftSideOverlay(theme: string): void {
  const leftSide = document.querySelector('.lg\\:w-1\\/2.relative.bg-cover') as HTMLElement;
  if (!leftSide) return;
  
  let overlay = leftSide.querySelector('.theme-overlay') as HTMLElement;
  
  if (theme === 'light') {
    // Add dark overlay for light theme
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'theme-overlay absolute inset-0 bg-black transition-opacity duration-1000';
      overlay.style.opacity = '0';
      leftSide.appendChild(overlay);
    }
    setTimeout(() => {
      overlay.style.opacity = '0.5';
    }, 10);
  } else {
    // Remove overlay for dark theme
    if (overlay) {
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
      }, 1000);
    }
  }
}

/**
 * Setup forgot password link click handler
 */
function setupForgotPasswordLink(): void {
  const forgotLink = document.getElementById('forgot-password-link');
  
  if (!forgotLink) {
    logger.warn('Forgot password link not found');
    return;
  }

  forgotLink.addEventListener('click', async (e) => {
    e.preventDefault();
    await showForgotPasswordModal();
  });
}

/**
 * Setup request access/signup link click handler
 */
function setupRequestAccessLink(): void {
  const requestLink = document.getElementById('request-access-link');
  
  if (!requestLink) {
    logger.warn('Request access link not found');
    return;
  }

  requestLink.addEventListener('click', async (e) => {
    e.preventDefault();
    await showSignupModal();
  });
}

/**
 * Update theme toggle icon based on current theme
 */
function updateIcon(button: HTMLElement, icon: HTMLElement, theme: string): void {
  icon.className = theme === 'dark' ? 'fas fa-moon text-xl' : 'fas fa-sun text-xl text-yellow-400';
  button.setAttribute('data-tip', theme === 'dark' ? 'Switch theme to light' : 'Switch theme to dark');
}

/**
 * Setup login form submission handler
 */
function setupLoginSubmit(): void {
  const form = document.getElementById('login-form') as HTMLFormElement;
  logger.debug('setupLoginSubmit', { form: !!form });
  
  if (!form) {
    logger.warn('Login form element not found');
    return;
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    
    // Show loading state
    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Signing in...';
    
    // Show authenticating toaster
    ToasterService.info('Authenticating...');
    
    // Small delay to ensure toaster renders
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const result = await authService.login({ email, password });
      
      if (result.success) {
        ToasterService.dismissAll();
        
        // Close the login overlay if it exists
        const loginOverlay = document.getElementById('login-overlay');
        if (loginOverlay) {
          loginOverlay.remove();
        }
        
        form.reset();
      } else {
        ToasterService.error(result.message || 'Login failed. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    } catch (error) {
      logger.error('Login error:', error);
      ToasterService.error('Login failed. Please check your credentials and try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
